/**
 * Issue #1021 (round 7, D204.2) — the strings and pure helpers behind the
 * per-milestone state control on `PlanMilestonesScreen.jsx`. The state
 * model itself (`not_started` | `going` | `done`, the order invariant) is
 * owned server-side by `api/src/lib/milestoneStates.ts` (issue #255,
 * PR #1017); nothing here re-derives it — `liveIndex` below reads the
 * array the server already sent, it does not decide what belongs in it.
 *
 * Kept pure and side-effect free, same discipline `milestoneStates.ts`
 * documents for itself: every function here is a plain function over
 * plain arrays, so it can be pinned directly without mounting anything.
 */

// D200.3 — three states in words, never a date, a percentage or a bar.
export const STATE_LABELS = {
  not_started: "Not started",
  going: "Going",
  done: "Done",
};

export const FIRST_UP = "First up";
export const NEXT_UP = "Next up";
export const WAITS = "Waits until the one above is done";
export const STARTING = "Starting…";
export const SAVING = "Saving…";
export const TRY_AGAIN = "Try again";

// D200.4 — the goal's own status is never touched here; this is
// commentary on the milestones only, not a status change.
export const ALL_DONE = (n) => `All ${n} done.`;
export const ALL_DONE_NOTE =
  "The goal itself is still In progress. When you're finished with it, mark it complete on your plan.";

/**
 * "Couldn't save that. It's still marked <label> — nothing has changed."
 * `state` is the STATE THAT IS ACTUALLY SAVED (never the one that was
 * tapped and failed — D57/the artboard's own "the control shows what is
 * saved, not what was tapped"). The drawn copy uses the label in lower
 * case mid-sentence ("going", not "Going"), which is `STATE_LABELS[state]`
 * lower-cased rather than a second, parallel string table that could drift
 * from the one the control itself renders.
 */
export function SAVE_FAILED(state) {
  const label = STATE_LABELS[state] ?? state;
  return `Couldn't save that. It's still marked ${label.toLowerCase()} — nothing has changed.`;
}

/**
 * D193.2 — "one at a time": the index of the first row that is NOT
 * `done`, or `-1` once every row is. Rows after this index (when it is not
 * `-1`) are the ones that wait; this index itself is the one row a player
 * can still act on that isn't already `done` (D193.2's worked example
 * allows undoing the last `done` too, which is why `-1` is a valid, fully
 * "live" state — see the components' own comment for how the two combine
 * into "is this row disabled").
 */
export function liveIndex(states) {
  for (let i = 0; i < states.length; i += 1) {
    if (states[i] !== "done") return i;
  }
  return -1;
}

/**
 * Round 2 review, F2 (Andreas's ruling, recorded for veto) — the artboard's
 * "next-up" commentary ("Done stays live... moving a done row back makes
 * it the one that is up again; the rows under it wait again") describes
 * undoing the LAST done row, and cannot be read as "any done row" against
 * the server's own order invariant (`isOrdered` in
 * `api/src/lib/milestoneStates.ts`): `done* going? not_started*`. Moving a
 * NON-last done row back to `going` (or `not_started`) would put a `done`
 * AFTER a `going`/`not_started`, which `isOrdered` rejects outright — the
 * server returns 409 `milestone_out_of_order` for exactly that tap today
 * (round 2 review evidence, `undoNonLast` in the reviewer's probe:
 * `{ status: 409, code: "milestone_out_of_order" }`). A control that
 * invites a tap the server is guaranteed to refuse is not read-only-until-
 * confirmed — it is honestly wrong, so only the one row the server WOULD
 * accept an undo from is offered as live.
 *
 * `undoableIndex` names that ONE row: the last `done` row, and ONLY while
 * the row immediately after it (the live row) is `not_started` — if the
 * live row is already `going`, moving the preceding `done` back would
 * create a second `going`, which `isOrdered` also rejects (at most one).
 * When every row is `done` (`liveIndex` is `-1`), the last row itself is
 * the one eligible for undo (there is no row after it to conflict with).
 * When nothing is `done` yet, there is nothing to undo: `-1`.
 */
export function undoableIndex(states) {
  const live = liveIndex(states);
  if (live === -1) return states.length > 0 ? states.length - 1 : -1;
  if (live === 0) return -1; // nothing done yet
  if (states[live] === "going") return -1; // undoing would create a second "going"
  return live - 1;
}

/**
 * Whether row `i` accepts a tap right now, independent of any in-flight
 * save (the component ANDs this with "no save is running anywhere" —
 * D193.2's "every segment on the page is inert" while one save runs,
 * round 2 review F1). Exactly two kinds of row are interactive:
 *
 *   - the live row (`liveIndex`'s own index) — whatever its state
 *     (`not_started`, about to start, or `going`, already under way);
 *   - the ONE undo-eligible `done` row (`undoableIndex`).
 *
 * Every other row — a `not_started` row past the live one (waiting,
 * D193.2), or a `done` row that is not the undo-eligible one (round 2
 * review F2) — is inert, drawn muted, exactly like a waiting row.
 */
export function isRowInteractive(states, i) {
  const live = liveIndex(states);
  if (i === live) return true;
  if (states[i] === "done") return i === undoableIndex(states);
  return false;
}
